const DB_NAME = 'deepgo-collection';
const DB_VERSION = 1;

export interface CollectionGame {
  id: string;
  filename: string;
  sgfText: string;
  playerBlack: string;
  playerWhite: string;
  result: string;
  date: string;
  event: string;
  komi: number;
  boardSize: number;
  moveCount: number;
  collection: string;   // e.g. folder name, or 'Unsorted'
  favorite: boolean;
  addedAt: number;
}

function getSgfProp(sgf: string, prop: string): string {
  const m = new RegExp(`${prop}\\[([^\\]]*)\\]`).exec(sgf);
  return m ? m[1].trim() : '';
}

/** Stable content-based ID — re-importing same file is idempotent */
function makeGameId(sgfText: string): string {
  const sample = sgfText.slice(0, 300) + sgfText.slice(-100) + sgfText.length;
  let h = 0;
  for (let i = 0; i < sample.length; i++) {
    h = Math.imul(31, h) + sample.charCodeAt(i) | 0;
  }
  return Math.abs(h).toString(36) + '_' + sgfText.length;
}

export function parseSgfMetadata(sgfText: string, filename: string, collection = 'Unsorted'): CollectionGame {
  const playerBlack = getSgfProp(sgfText, 'PB') || 'Black';
  const playerWhite = getSgfProp(sgfText, 'PW') || 'White';
  const result      = getSgfProp(sgfText, 'RE');
  const date        = getSgfProp(sgfText, 'DT');
  const event       = getSgfProp(sgfText, 'EV');
  const komi        = parseFloat(getSgfProp(sgfText, 'KM')) || 7.5;
  const boardSize   = parseInt(getSgfProp(sgfText, 'SZ')) || 19;
  const moveCount   = (sgfText.match(/;[BW]\[/g) || []).length;

  return {
    id: makeGameId(sgfText),
    filename,
    sgfText,
    playerBlack,
    playerWhite,
    result,
    date,
    event,
    komi,
    boardSize,
    moveCount,
    collection,
    favorite: false,
    addedAt: Date.now(),
  };
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = (e.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains('games')) {
        db.createObjectStore('games', { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror  = () => reject(req.error);
  });
}

export async function addGame(game: CollectionGame): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('games', 'readwrite');
    tx.objectStore('games').put(game);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}

export async function loadAllGames(): Promise<CollectionGame[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx  = db.transaction('games', 'readonly');
    const req = tx.objectStore('games').getAll();
    req.onsuccess = () => resolve(req.result as CollectionGame[]);
    req.onerror   = () => reject(req.error);
  });
}

export async function updateGame(id: string, updates: Partial<CollectionGame>): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx    = db.transaction('games', 'readwrite');
    const store = tx.objectStore('games');
    const req   = store.get(id);
    req.onsuccess = () => {
      if (req.result) store.put({ ...req.result, ...updates });
      resolve();
    };
    req.onerror = () => reject(req.error);
  });
}

export async function removeGame(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('games', 'readwrite');
    tx.objectStore('games').delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror    = () => reject(tx.error);
  });
}
